import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismRadioGroup } from '@mylighthouse/prism-ember';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t, tKey } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import type { DateGranularity } from 'lumen/utils/constants/budget-forecast-constants';
import { formatMonthDateRange } from 'lumen/utils/dates/format-month-date-range';
import { localCopy } from 'tracked-toolbox';

import styles from './datepicker.module.css';

const DAY_GRANULARITY_OPTIONS = [
  {
    id: 'last_month',
    text: tKey('general.last_month'),
    range: {
      start: dayjs().subtract(1, 'month').startOf('month'),
      end: dayjs().subtract(1, 'month').endOf('month'),
    },
  },
  {
    id: 'this_month',
    text: tKey('general.this_month'),
    range: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month'),
    },
  },
  {
    id: 'next_month',
    text: tKey('general.month.next'),
    range: {
      start: dayjs().add(1, 'month').startOf('month'),
      end: dayjs().add(1, 'month').endOf('month'),
    },
  },
  {
    id: 'custom',
    text: tKey('general.custom'),
    range: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month'),
    },
  },
];

const MONTH_GRANULARITY_OPTIONS = [
  {
    id: 'last_year',
    text: tKey('general.last_year'),
    range: {
      start: dayjs().subtract(1, 'year').startOf('year'),
      end: dayjs().subtract(1, 'year').endOf('year'),
    },
  },
  {
    id: 'this_year',
    text: tKey('general.this_year'),
    range: {
      start: dayjs().startOf('year'),
      end: dayjs().endOf('year'),
    },
  },
  {
    id: 'next_12_months',
    text: tKey('general.next_12_months'),
    range: {
      start: dayjs().startOf('month'),
      end: dayjs().add(11, 'month').endOf('month'),
    },
  },
  {
    id: 'next_year',
    text: tKey('general.next_year'),
    range: {
      start: dayjs().add(1, 'year').startOf('year'),
      end: dayjs().add(1, 'year').endOf('year'),
    },
  },
  {
    id: 'custom',
    text: tKey('general.custom'),
    range: {
      start: dayjs().startOf('year'),
      end: dayjs().endOf('year'),
    },
  },
];

interface SettingsBudgetForecastDatepickerSignature {
  Args: {
    disabledTooltipMessage?: string;
    endDate: string;
    granularity: DateGranularity;
    handleDateRangeChange: (
      granularity: DateGranularity,
      startDate: string,
      endDate: string,
    ) => void;
    isDisabled?: boolean;
    startDate: string;
  };
}

export default class SettingsBudgetForecastDatepicker extends Component<SettingsBudgetForecastDatepickerSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  /**
   * Temporary start date used to calculate the max range.
   * Set when user selects a start date without an end date.
   * Cleared when user selects an end date.
   */
  @tracked tempStartDate: Dayjs | null = null;

  @localCopy('args.endDate') declare endDate: string | null;
  @localCopy('args.granularity') declare granularity: DateGranularity;

  @localCopy('args.startDate') declare startDate: string | null;

  get dateRange(): { end: Dayjs; start: Dayjs } {
    return {
      start: this.hotelAndMarketService.toLocalTime(this.startDate),
      end: this.hotelAndMarketService.toLocalTime(this.endDate),
    };
  }

  get footerMessage(): string {
    return this.granularity === 'day'
      ? this.intl.t('settings.budget_forecast.max_31_days')
      : this.intl.t('settings.budget_forecast.max_12_months');
  }

  get isApplyDisabled(): boolean {
    // If only a start date is selected (no end date), we treat it as selecting the entire month.
    // However, if this auto-expanded range matches the current selection, disable the apply button.
    const isTempStartDateChanged =
      this.tempStartDate &&
      (this.tempStartDate.startOf('month').format('YYYY-MM-DD') !==
        this.args.startDate ||
        this.tempStartDate.endOf('month').format('YYYY-MM-DD') !==
          this.args.endDate);

    return (
      this.granularity === this.args.granularity &&
      this.startDate === this.args.startDate &&
      this.endDate === this.args.endDate &&
      !isTempStartDateChanged
    );
  }

  get maxDate(): Dayjs {
    return dayjs().add(2, 'year').endOf('year');
  }

  get maxRange(): string | number {
    if (this.granularity === 'day') {
      return '1 month';
    }

    if (!this.tempStartDate) {
      return '1 year';
    }

    // If the user has selected a start date, we need to calculate the max range
    // based on the selected start date.
    // e.g. any day in January 2024 should result in max date of 31 December 2024
    const endDate = this.tempStartDate.add(11, 'month').endOf('month');
    return endDate.diff(this.tempStartDate, 'day');
  }

  get sidebarLists(): {
    options: typeof DAY_GRANULARITY_OPTIONS | typeof MONTH_GRANULARITY_OPTIONS;
    title: string;
  }[] {
    return [
      {
        title: this.intl.t('settings.budget_forecast.suggested_periods'),
        options:
          this.granularity === 'day'
            ? DAY_GRANULARITY_OPTIONS
            : MONTH_GRANULARITY_OPTIONS,
      },
    ];
  }

  @action
  changeGranularity(event: Event): void {
    const selectedGranularity = (event.target as HTMLInputElement).value;
    this.granularity = selectedGranularity as DateGranularity;

    if (this.granularity === 'day') {
      this.usageAnalytics.track('Daily Granularity Selected', {
        eventTrigger: 'daily-view-clicked',
      });
      this.startDate = dayjs().startOf('month').format('YYYY-MM-DD');
      this.endDate = dayjs().endOf('month').format('YYYY-MM-DD');
    } else {
      this.usageAnalytics.track('Monthly Granularity Selected', {
        eventTrigger: 'monthly-view-clicked',
      });
      this.startDate = dayjs().startOf('year').format('YYYY-MM-DD');
      this.endDate = dayjs().endOf('year').format('YYYY-MM-DD');
    }
  }

  @action
  monthTriggerLabelFormatter(
    start: Dayjs | null | undefined,
    end: Dayjs | null | undefined,
  ): string {
    return formatMonthDateRange(
      start,
      end,
      this.dates.partialDateFormat.YEAR_AND_MONTH_LONG,
    );
  }

  @action
  onCancel(): void {
    this.granularity = this.args.granularity;
    this.startDate = this.args.startDate;
    this.endDate = this.args.endDate;
  }

  @action
  updateDateRange(
    dateRange: { end: Dayjs | null; start: Dayjs },
    rangeId?: string,
  ): void {
    const start =
      this.granularity === 'day'
        ? dateRange.start
        : dateRange.start.startOf('month');
    const endWithFallback = dateRange.end ?? start.endOf('month');

    const formattedStart = start.format('YYYY-MM-DD');
    const formattedEnd = endWithFallback.format('YYYY-MM-DD');

    this.args.handleDateRangeChange(
      this.granularity,
      formattedStart,
      formattedEnd,
    );

    if (!rangeId || rangeId === 'custom') {
      this.usageAnalytics.track('Date Range Set Manually', {
        eventTrigger: 'date-range-changed',
      });
    } else {
      this.usageAnalytics.track('Pre-defined Date Range Selected', {
        eventTrigger: 'predefined-date-range-clicked',
      });
    }
  }

  @action
  updateTempDateRange(dateRange: { end: Dayjs | null; start: Dayjs }): void {
    if (!dateRange.end) {
      this.tempStartDate = dateRange.start;
      return;
    }

    this.tempStartDate = null;

    if (this.granularity === 'day') {
      this.startDate = dateRange.start.format('YYYY-MM-DD');
      this.endDate = dateRange.end.format('YYYY-MM-DD');
    } else {
      this.startDate = dateRange.start.startOf('month').format('YYYY-MM-DD');
      this.endDate = dateRange.end.endOf('month').format('YYYY-MM-DD');
    }
  }

  <template>
    <div
      class={{concat styles.datepicker " " "text-300--regular relative"}}
      data-test-id="budget-forecast-datepicker"
    >
      {{! @glint-expect-error: Incorrect type }}
      <GeneralDatepickersDatepickerWithSidebar
        @disabledTooltipMessage={{@disabledTooltipMessage}}
        @footerMessage={{this.footerMessage}}
        @isApplyDisabled={{this.isApplyDisabled}}
        @isDisabled={{@isDisabled}}
        @maxDate={{this.maxDate}}
        @maxRange={{this.maxRange}}
        @onCancel={{this.onCancel}}
        @onUpdateTempRange={{this.updateTempDateRange}}
        @range={{this.dateRange}}
        @rangeChanged={{this.updateDateRange}}
        @sidebarLists={{this.sidebarLists}}
        @triggerClass="ic-product-tour-48864127-datepicker ic-product-tour-49285022-datepicker"
        @triggerLabelFormatter={{if
          (eq this.granularity "month")
          this.monthTriggerLabelFormatter
        }}
      >
        <:default as |beforeContent|>
          <div class="flex flex-col gap-600 p-400 pb-0">
            <div>
              <p class="text-200--bold mb-100">
                {{t "settings.budget_forecast.select_granularity"}}
              </p>
              <PrismRadioGroup
                @isHorizontal={{true}}
                @name="granularity"
                @onChange={{queue
                  this.changeGranularity
                  beforeContent.refocusCalendar
                }}
                @value={{this.granularity}}
                data-test-id="granularity"
                as |RadioGroup|
              >
                <RadioGroup.input @value="day" data-test-id="granularity-day">
                  {{t "general.day"}}
                </RadioGroup.input>
                <RadioGroup.input
                  @value="month"
                  data-test-id="granularity-month"
                >
                  {{t "general.month"}}
                </RadioGroup.input>
              </PrismRadioGroup>
            </div>

            <p class="text-200--bold">
              {{t "settings.budget_forecast.select_period"}}
            </p>
          </div>
        </:default>
      </GeneralDatepickersDatepickerWithSidebar>
    </div>
  </template>
}
